import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Typography,
  Button,
  Grid,
  TextField,
  InputAdornment,
  Drawer,
  Divider,
  Autocomplete,
} from "@mui/material";
import "../../App.css";
import VisibilityIcon from "@mui/icons-material/Visibility";
import BranchIcon from "@mui/icons-material/AccountTree";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import FilterListIcon from "@mui/icons-material/FilterList";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import { useNavigate } from "react-router-dom";
import { TableNodata } from "../../components/TableNodata";
import { TableLoader } from "../../components/TableLoader";
import { useQuery } from "@tanstack/react-query";
import api from "../../api/client";
import CustomPagination from "../../components/CustomPagination";
import CreateBatch from "./CreateBatch";
import { useChangeableItems, getLabelByKey } from "../../store/useDynamicLabel";

const BatchesList = () => {

  const changeableItems = useChangeableItems();
  const getLabel = (itemKey) => getLabelByKey(changeableItems, itemKey);

  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [openDrawer, setOpenDrawer] = useState(false);
  const [appliedInstitute, setAppliedInstitute] = useState(null);
  const [selectedInstitute, setSelectedInstitute] = useState(null);
  const [instituteOptions, setInstituteOptions] = useState([]);
  const [batchOptions, setBatchOptions] = useState([]);
  const [appliedBatchName, setAppliedBatchName] = useState(null);
  const [selectedBatchName, setSelectedBatchName] = useState(null);
  const [appliedStatus, setAppliedStatus] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [selectedBatchId, setSelectedBatchId] = useState(null);

  const fetchBatchesList = async () => {
    try {
      const payload = {
        page: page,
        size: rowsPerPage,
        search_key: debouncedSearch || "",
        status: appliedStatus || "",
        institute_id: appliedInstitute?.institute_id || null,
        institute_name: appliedInstitute?.institute_name || "",
        batch_id: appliedBatchName?.batch_id || null,
      };
      const res = await api.post("GetAllBatches", payload);
      if (res?.data?.status === "success") {
        return {
          data: res?.data?.data || [],
          total: res?.data?.total || res?.data?.data?.length || 0,
        };
      }
      return {
        data: [],
        total: 0,
      };
    } catch (error) {
      console.log("Error:", error);
      return {
        data: [],
        total: 0,
      };
    }
  };

  const { data: queryData, isLoading, refetch } = useQuery({
    queryKey: ["batchesList", page, rowsPerPage, debouncedSearch, appliedInstitute, appliedBatchName, appliedStatus],
    queryFn: fetchBatchesList,
  });

  const batchesData = queryData?.data || [];
  const count = queryData?.total || 0;

  // Load search filters from localStorage on mount
  useEffect(() => {
    const savedSearch = localStorage.getItem("batchesListSearch");
    const savedInstitute = localStorage.getItem("batchesListInstitute");
    const savedBatchName = localStorage.getItem("batchesListBatchName");
    const savedStatus = localStorage.getItem("batchesListStatus");

    if (savedSearch) setSearch(savedSearch);
    if (savedInstitute) setAppliedInstitute(JSON.parse(savedInstitute));
    if (savedBatchName) setAppliedBatchName(JSON.parse(savedBatchName));
    if (savedStatus) setAppliedStatus(savedStatus);
  }, []);

  // Save search filters to localStorage when they change
  useEffect(() => {
    localStorage.setItem("batchesListSearch", search);
  }, [search]);

  useEffect(() => {
    if (appliedInstitute) {
      localStorage.setItem("batchesListInstitute", JSON.stringify(appliedInstitute));
    } else {
      localStorage.removeItem("batchesListInstitute");
    }
  }, [appliedInstitute]);

  useEffect(() => {
    if (appliedBatchName) {
      localStorage.setItem("batchesListBatchName", JSON.stringify(appliedBatchName));
    } else {
      localStorage.removeItem("batchesListBatchName");
    }
  }, [appliedBatchName]);

  useEffect(() => {
    if (appliedStatus) {
      localStorage.setItem("batchesListStatus", appliedStatus);
    } else {
      localStorage.removeItem("batchesListStatus");
    }
  }, [appliedStatus]);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);

    return () => clearTimeout(timer);
  }, [search]);

  // Fetch institute options for filter drawer
  useEffect(() => {
    const fetchInstituteOptions = async () => {
      try {
        const { data } = await api.post("ListInstituteNames");
        setInstituteOptions(data?.data ?? []);
      } catch (error) {
        console.error("Error fetching institute options:", error);
      }
    };

    fetchInstituteOptions();
  }, []);

  // Fetch batch options for filter drawer
  useEffect(() => {
    const fetchBatchOptions = async () => {
      try {
        const { data } = await api.post("getAllBatchList");
        setBatchOptions(data?.data ?? []);
      } catch (error) {
        console.error("Error fetching batch options:", error);
      }
    };

    fetchBatchOptions();
  }, []);

  const headings = [
    { label: "S.No", align: "center" },
    { label: `${getLabel('BATCHES')}`, align: "left" },
    ...(localStorage.getItem("role") === "SUPER_ADMIN" ? [{ label: "Institute Name", align: "left" }] : []),
    { label: "Created By", align: "left" },
    { label: "Created Date", align: "left" },
    { label: "Status", align: "left" },
    { label: "Action", align: "center" },
  ];

  const getStatusColor = (status) => {
    const lowerStatus = status?.toLowerCase();
    switch (lowerStatus) {
      case "active":
        return { backgroundColor: "#d4edda", color: "#155724" };
      case "inactive":
        return { backgroundColor: "#f8d7da", color: "#721c24" };
      default:
        return { backgroundColor: "#f1f1f1", color: "#333" };
    }
  };

  const handleView = (batch) => {
    navigate("/viewBatch", {
      state: {
        BatchId: batch.batch_id,
      },
    });
  };

  const handleEdit = (batch) => {
    setSelectedBatchId(batch.batch_id);
    setOpenCreateDialog(true);
  };

  return (
    <Box sx={{ minHeight: "100vh" }}>
      <Box
        sx={{
          p: 3,
          backgroundColor: "#fff",
          borderRadius: "12px",
          backgroundColor: "#fafbfc"
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <BranchIcon sx={{ fontSize: "32px", color: "#1976d2" }} />
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  color: "#1c2434",
                }}
              >
                {getLabel('BATCHES')} List
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: "flex", gap: 4, alignItems: "center", mb: 2 }}>
            <TextField
              value={search}
              size="small"
              variant="outlined"
              placeholder={`Search ${getLabel('BATCHES')}`}
              onChange={(e) => {
                setPage(0);
                setSearch(e.target.value);
              }}
              className="custom-search-field"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />

            <Button
              variant="contained"
              startIcon={<AddIcon />}
              className="gradient-button"
              onClick={() => {
                setSelectedBatchId(null);
                setOpenCreateDialog(true);
              }}
            >
              Create {getLabel('BATCHES')} 
            </Button>
          </Box>
        </Box>
        <TableContainer
          component={Paper}
          sx={{
            boxShadow: '0 2px 10px rgba(0,0,0,0.02)',
          }}
        >
          <Table>
            <TableHead>
              <TableRow>
                <TableCell colSpan={headings.length}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                      {appliedInstitute && (
                        <Box className="filter-chip">
                          <Typography sx={{ fontSize: "13px", fontWeight: 500 }}>
                            {appliedInstitute?.institute_name || appliedInstitute}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => {
                              setAppliedInstitute(null);
                              setSelectedInstitute(null);
                              setPage(0);
                            }}
                            sx={{ ml: 0.5 }}
                          >
                            <CloseIcon sx={{ fontSize: "14px" }} />
                          </IconButton>
                        </Box>
                      )}
                      {appliedBatchName && (
                        <Box className="filter-chip">
                          <Typography sx={{ fontSize: "13px", fontWeight: 500 }}>
                            {appliedBatchName?.batch_name}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => {
                              setAppliedBatchName(null);
                              setSelectedBatchName(null);
                              setPage(0);
                            }}
                            sx={{ ml: 0.5 }}
                          >
                            <CloseIcon sx={{ fontSize: "14px" }} />
                          </IconButton>
                        </Box>
                      )}
                      {appliedStatus && (
                        <Box className="filter-chip">
                          <Typography sx={{ fontSize: "13px", fontWeight: 500 }}>
                            {appliedStatus}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => {
                              setAppliedStatus(null);
                              setSelectedStatus(null);
                              setPage(0);
                            }}
                            sx={{ ml: 0.5 }}
                          >
                            <CloseIcon sx={{ fontSize: "14px" }} />
                          </IconButton>
                        </Box>
                      )}
                    </Box>

                    <Box sx={{ display: "flex", gap: 1 }}>
                      <IconButton onClick={() => setOpenDrawer(true)} color="primary" className="more-filter-btn">
                        <FilterListIcon />
                        <Typography className="more-filter-text">More filter</Typography>
                      </IconButton>

                      {(appliedInstitute || appliedBatchName || appliedStatus) && (
                        <Button variant="outlined" className="clear-filter-btn" onClick={() => {
                          setAppliedInstitute(null);
                          setSelectedInstitute(null);
                          setAppliedBatchName(null);
                          setSelectedBatchName(null);
                          setAppliedStatus(null);
                          setSelectedStatus(null);
                          setPage(0);
                        }}>
                          Clear Filter
                        </Button>
                      )}
                    </Box>
                  </Box>
                </TableCell>
              </TableRow>
              <TableRow className="tableHeader">
                {headings.map((heading, index) => (
                  <TableCell
                    key={index}
                    align={heading.align}
                    className="tableHeader"
                  >
                    <b>{heading.label}</b>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            {isLoading ? (
              <TableCell colSpan={headings.length} align="center">
                <TableLoader message="Fetching batches..." />
              </TableCell>
            ) : (
              <TableBody className="tablebody">
                {batchesData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={headings.length}>
                      <TableNodata />
                    </TableCell>
                  </TableRow>
                ) : (
                  batchesData.map((batch, index) => (
                    <TableRow
                      key={batch.batch_id || index}
                      hover
                      sx={{
                        backgroundColor: index % 2 === 1 ? "#FAFAFA" : "inherit",
                        "&:hover": { backgroundColor: "#f1f1f1" },
                      }}
                    >
                      <TableCell align="center">{page * rowsPerPage + index + 1}</TableCell>
                      <TableCell sx={{ fontWeight: 500, color: "#1c2434" }}>
                        {batch.batch_name}
                      </TableCell>
                      {localStorage.getItem("role") === "SUPER_ADMIN" && (
                        <TableCell sx={{ color: "#475569" }}>
                          {batch.institute_name || "N/A"}
                        </TableCell>
                      )}
                      <TableCell sx={{ color: "#475569" }}>
                        {batch.created_by_name}
                      </TableCell>
                      <TableCell sx={{ color: "#475569" }}>
                        {new Date(batch.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell align="center">
                        <Box
                          sx={{
                            px: 2,
                            py: 0.5,
                            borderRadius: "12px",
                            fontWeight: "bold",
                            fontSize: "12px",
                            width: "fit-content",
                            ...getStatusColor(batch.status),
                            textAlign: "center",
                            textTransform: "capitalize"
                          }}
                        >
                          {batch.status?.charAt(0).toUpperCase() + batch.status?.slice(1).toLowerCase()}
                        </Box>
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{
                          padding: "10px 10px",
                          whiteSpace: "nowrap",
                          width: "1%",
                        }}
                      >
                        <Box display="flex" justifyContent="left" alignItems="center" gap={2}>
                          <IconButton
                            size="small"
                            onClick={() => handleView(batch)}
                            sx={{ display: "flex", gap: 0.5, p: 0 }}
                          >
                            <VisibilityIcon color="primary" />
                            <Typography color="primary" sx={{ fontSize: "12px" }}>
                              View
                            </Typography>
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleEdit(batch)}
                            sx={{ display: "flex", alignItems: "center", gap: 0.5, p: 0 }}
                          >
                            <EditIcon color="primary" />
                            <Typography color="primary" sx={{ fontSize: "12px" }}>
                              Edit
                            </Typography>
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
                {((page === 0 && batchesData?.length > rowsPerPage - 1) ||
                  page !== 0) && (
                    <CustomPagination
                      page={page}
                      rowsPerPage={rowsPerPage}
                      count={count}
                      setPage={setPage}
                      colSpan={headings.length}
                    />
                  )}
              </TableBody>
            )}
          </Table>
        </TableContainer>

        {/* Filter Drawer */}
        <Drawer
          anchor="right"
          open={openDrawer}
          onClose={() => setOpenDrawer(false)}
          PaperProps={{
            sx: {
              width: 320,
              p: 3,
              display: "flex",
              flexDirection: "column",
              gap: 2.5,
              height: "100vh",
              borderRadius: "12px 0 0 12px",
            },
          }}
        >
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={1}>
              <FilterListIcon color="primary" />
              <Typography variant="h6" fontWeight={600} color="primary">
                Filters
              </Typography>
            </Box>
            <IconButton aria-label="Close filters" size="small" onClick={() => setOpenDrawer(false)}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Divider />

          {localStorage.getItem("role") === "SUPER_ADMIN" && (
            <Box display="flex" flexDirection="column" gap={0.8}>
              <Typography variant="subtitle2" fontWeight={600}>Institute</Typography>
              <Autocomplete
                size="small"
                options={instituteOptions}
                getOptionLabel={(option) => option?.institute_name || option || ""}
                value={selectedInstitute}
                onChange={(_, newValue) => setSelectedInstitute(newValue)}
                renderInput={(params) => <TextField {...params} placeholder="All Institutes" fullWidth variant="outlined" />}
              />
            </Box>
          )}

          <Box display="flex" flexDirection="column" gap={0.8}>
            <Typography variant="subtitle2" fontWeight={600}>{getLabel('BATCHES')} Name</Typography>
            <Autocomplete
              size="small"
              options={batchOptions}
              getOptionLabel={(option) => option?.batch_name || ""}
              value={selectedBatchName}
              onChange={(_, newValue) => setSelectedBatchName(newValue)}
              renderInput={(params) => <TextField {...params} placeholder={`All ${getLabel('BATCHES')}`} fullWidth variant="outlined" />}
            />
          </Box>

          <Box display="flex" flexDirection="column" gap={0.8}>
            <Typography variant="subtitle2" fontWeight={600}>Status</Typography>
            <Autocomplete
              size="small"
              options={["Active", "Inactive"]}
              value={selectedStatus}
              onChange={(_, newValue) => setSelectedStatus(newValue)}
              renderInput={(params) => <TextField {...params} placeholder="All Statuses" fullWidth variant="outlined" />}
            />
          </Box>

          <Button
            variant="contained"
            color="primary"
            fullWidth
            className="customButton"
            onClick={() => {
              setAppliedInstitute(selectedInstitute);
              setAppliedBatchName(selectedBatchName);
              setAppliedStatus(selectedStatus || "");
              setPage(0);
              setOpenDrawer(false);
            }}
            sx={{ mt: 2 }}
          >
            Apply Filters
          </Button>
        </Drawer>
      </Box>

      <CreateBatch
        open={openCreateDialog}
        onClose={() => {
          setOpenCreateDialog(false);
          setSelectedBatchId(null);
        }}
        onSuccess={() => refetch()}
        BatchId={selectedBatchId}
      />
    </Box>
  );
};

export default BatchesList;
